import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import itemsApi from '../itemsApi';
import { loadSalt } from '../authentication/authenticationReducer';
import { addSuccessNotification, addErrorNotification } from '../common/notification/notificationReducer';
import transformer from '../common/helpers/transformer';
import type { Child } from '../common/types/child';
import type { DecryptedItem } from '../common/types/items';
import type { AppDispatch, ChildrenState, Store } from '../store';
import { ChildPayloadType } from '../common/types/general';

const initialState: ChildrenState = {
  children: [],
  currentChild: null,
  status: null,
  errorMessage: null,
};

const loadChildren = createAsyncThunk<
  DecryptedItem<Child>[],
  undefined,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('children/loadChildren', async (_, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  await dispatch(loadSalt());
  const data = await itemsApi.loadItems('child');
  if (data != null) {
    return await Promise.all(
      data.items.map(async (item): Promise<DecryptedItem<Child>> => {
        try {
          const child = await transformer.fromBase64ToObjectWithDecryption<Child>(item.payload);
          return {
            ...item,
            payload: child,
          };
        } catch (error) {
          console.log('Fehler: ', error);
          dispatch(addErrorNotification('Kind konnte nicht entschlüsselt werden.'));
          return {
            ...item,
            payload: {},
          };
        }
      }),
    );
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Kinder konnten nicht geladen werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return [];
});

const loadChildById = createAsyncThunk<
  DecryptedItem<Child> | null,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('children/loadChildById', async (id: string, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const data = await itemsApi.loadItemById(id);
  if (data != null && data.item != null) {
    const child = await transformer.fromBase64ToObjectWithDecryption<Child>(data.item.payload);
    return {
      ...data.item,
      payload: child,
    };
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Das Kind mit der ID ${id} konnte nicht geladen werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const addChild = createAsyncThunk<
  string | null,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('children/addChild', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const item = await itemsApi.addItem(id, payload, 'child');
  if (item != null) {
    dispatch(addSuccessNotification('Das Kind wurde hinzugefügt.'));
    return item.id;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Das Kind mit der ID ${id} konnte nicht hinzugefügt werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return null;
});

const editChild = createAsyncThunk<
  boolean,
  { id: string; payload: string },
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('children/editChild', async ({ id, payload }, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isEdited = await itemsApi.editItem(id, payload);
  if (isEdited) {
    dispatch(addSuccessNotification('Die Änderungen wurden gespeichert.'));
    return isEdited;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = 'Die Änderungen konnten nicht gespeichert werden.';
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const deleteChild = createAsyncThunk<
  boolean,
  string,
  {
    state: Store;
    dispatch: AppDispatch;
    rejectValue: string;
  }
>('children/deleteChild', async (id, { dispatch, getState, rejectWithValue }) => {
  const state = getState();
  const isDeleted = await itemsApi.deleteItem(id);
  if (isDeleted) {
    dispatch(addSuccessNotification('Das Kind wurde gelöscht.'));
    return isDeleted;
  }
  if (state.authentication.isInitialized) {
    const errorMessage = `Das Kind mit der ID ${id} konnte nicht gelöscht werden.`;
    dispatch(addErrorNotification(errorMessage));
    return rejectWithValue(errorMessage);
  }
  return false;
});

const childrenSlice = createSlice({
  name: 'children',
  initialState,
  reducers: {
    clearCurrentChild(state) {
      state.currentChild = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadChildren.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadChildren.fulfilled, (state, action) => {
        state.status = 'idle';
        state.children = action.payload;
      })
      .addCase(loadChildren.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(loadChildById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(loadChildById.fulfilled, (state, action) => {
        state.status = 'idle';
        state.currentChild = action.payload;
      })
      .addCase(loadChildById.rejected, (state, action) => {
        state.status = 'idle';
        state.currentChild = null;
        state.errorMessage = action.payload;
      })
      .addCase(addChild.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addChild.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(addChild.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(editChild.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(editChild.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(editChild.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase(deleteChild.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteChild.fulfilled, (state) => {
        state.status = 'idle';
      })
      .addCase(deleteChild.rejected, (state, action) => {
        state.status = 'idle';
        state.errorMessage = action.payload;
      })
      .addCase('resetState', (state) => {
        state = initialState;
      });
  },
});

const { clearCurrentChild } = childrenSlice.actions;

export { loadChildren, loadChildById, clearCurrentChild, addChild, editChild, deleteChild };

export default childrenSlice.reducer;
